export interface TreeNode {
  path: any[];
  level: number;
  children: TreeNode[];
}

/**
 * Builds a tree structure from flat hierarchical enum values.
 * Each value is an array like ["Equity", "Long/Short", "US"].
 * Creates intermediate nodes for each prefix and establishes parent-child relationships.
 */
export function buildTree(enumValues: any[]): TreeNode[] {
  const root: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  enumValues.forEach((value) => {
    if (!Array.isArray(value)) return;

    for (let i = 1; i <= value.length; i++) {
      const path = value.slice(0, i);
      const key = JSON.stringify(path);

      if (!nodeMap.has(key)) {
        const node: TreeNode = {
          path,
          level: i - 1,
          children: [],
        };
        nodeMap.set(key, node);

        if (i === 1) {
          root.push(node);
        } else {
          const parentKey = JSON.stringify(value.slice(0, i - 1));
          const parent = nodeMap.get(parentKey);
          if (parent) {
            parent.children.push(node);
          }
        }
      }
    }
  });

  // Stored order, at every level. The tree used to alphabetise each level,
  // which made the order an organization gave its options invisible on every
  // hierarchical surface and left the editor's reordering with nothing to
  // show for it (#11846, item 13).
  return root;
}

/**
 * Filters a tree based on a search query.
 * Includes nodes that match the query or have matching descendants.
 */
export function filterTree(tree: TreeNode[], searchQuery: string): TreeNode[] {
  if (!searchQuery.trim()) return tree;

  const searchLower = searchQuery.toLowerCase();

  const nodeMatches = (node: TreeNode): boolean => {
    const displayValue = String(node.path[node.path.length - 1]).toLowerCase();
    return displayValue.includes(searchLower);
  };

  const hasMatchingDescendant = (node: TreeNode): boolean => {
    if (nodeMatches(node)) return true;
    return node.children.some((child) => hasMatchingDescendant(child));
  };

  const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes
      .filter((node) => nodeMatches(node) || hasMatchingDescendant(node))
      .map((node) => ({
        ...node,
        children: filterNodes(node.children),
      }));
  };

  return filterNodes(tree);
}

/**
 * Checks if a node's path exists as a full value in the provided options.
 * Used to determine which tree nodes are selectable (leaf values) vs
 * intermediate-only nodes.
 */
export function isLeafValue(node: TreeNode, allValues: any[]): boolean {
  const nodePath = JSON.stringify(node.path);
  return allValues.some(
    (value) => Array.isArray(value) && JSON.stringify(value) === nodePath
  );
}

/**
 * Prunes a tree by removing nodes that match filterExisting and
 * removing empty intermediate branches.
 */
export function pruneTree(
  tree: TreeNode[],
  filterExisting: any[],
  allValues: any[]
): TreeNode[] {
  const existingSet = new Set(filterExisting.map((v) => JSON.stringify(v)));

  const pruneNodes = (nodes: TreeNode[]): TreeNode[] => {
    return nodes
      .map((node) => ({
        ...node,
        children: pruneNodes(node.children),
      }))
      .filter((node) => {
        const isValue = isLeafValue(node, allValues);
        const isFiltered = existingSet.has(JSON.stringify(node.path));

        // If this is a selectable value and it's already selected, hide it
        // (but only if it has no remaining children)
        if (isValue && isFiltered && node.children.length === 0) {
          return false;
        }

        // Keep intermediate nodes that still have children
        if (!isValue && node.children.length === 0) {
          return false;
        }

        return true;
      });
  };

  return pruneNodes(tree);
}
